/**
 * Feature Flags — Système d'isolation pour déploiement progressif
 * 
 * Usage:
 *   import { isFeatureEnabled, FEATURES } from '../config/featureFlags';
 *   if (isFeatureEnabled(FEATURES.MISSION_CONTROL)) { ... }
 * 
 * Activation:
 *   - Via localStorage: localStorage.setItem('ff_mission_control', 'true')
 *   - Via env: REACT_APP_FF_MISSION_CONTROL=true
 *   - Via code: modifier les defaults ci-dessous
 * 
 * Sécurité: les flags n'affectent QUE le frontend, pas les APIs.
 */

export const FEATURES = {
  MISSION_CONTROL: 'mission_control',        // Dashboard orienté valeur/persona
  SAFE_CHANGE_FLOW: 'safe_change_flow',      // Parcours impact → tests → export
  PERSONA_ROUTING: 'persona_routing',        // Navigation guidée par rôle
  VALUE_KPIS: 'value_kpis',                  // KPIs de valeur (risques évités, etc.)
};

// Defaults — mettre à true pour activer globalement
const FLAG_DEFAULTS = {
  [FEATURES.MISSION_CONTROL]: true,          // ✅ Sprint 1 — activé
  [FEATURES.SAFE_CHANGE_FLOW]: true,         // ✅ Sprint 3 — activé
  [FEATURES.PERSONA_ROUTING]: false,         // Sprint 2
  [FEATURES.VALUE_KPIS]: false,              // Sprint 3
};

/**
 * Vérifie si une feature est activée.
 * Priorité : localStorage > env var > default
 */
export const isFeatureEnabled = (featureName) => {
  // 1. localStorage override (pour testing / beta)
  const localOverride = localStorage.getItem(`ff_${featureName}`);
  if (localOverride !== null) {
    return localOverride === 'true';
  }

  // 2. Environment variable
  const envKey = `REACT_APP_FF_${featureName.toUpperCase()}`;
  const envVal = process.env[envKey];
  if (envVal !== undefined) {
    return envVal === 'true';
  }

  // 3. Default
  return FLAG_DEFAULTS[featureName] ?? false;
};

/**
 * Active/désactive un flag temporairement (session courante)
 */
export const setFeatureFlag = (featureName, enabled) => {
  localStorage.setItem(`ff_${featureName}`, String(enabled));
};

/**
 * Réinitialise un flag à sa valeur par défaut
 */
export const resetFeatureFlag = (featureName) => {
  localStorage.removeItem(`ff_${featureName}`);
};

/**
 * Liste tous les flags et leur statut actuel
 */
export const getAllFlags = () => {
  return Object.entries(FEATURES).map(([key, name]) => ({
    key,
    name,
    enabled: isFeatureEnabled(name),
    source: localStorage.getItem(`ff_${name}`) !== null ? 'localStorage'
      : process.env[`REACT_APP_FF_${name.toUpperCase()}`] !== undefined ? 'env'
      : 'default'
  }));
};

export default { FEATURES, isFeatureEnabled, setFeatureFlag, resetFeatureFlag, getAllFlags };
